import React, { useState, useEffect } from 'react';
import { Grid, Drawer, Typography, Button, makeStyles, Divider, Chip } from '@material-ui/core';
import FieldTestCards from 'components/Inspections/FieldTestCards';
import ReviewInspectionForm from 'components/Inspections/ReviewInspectionForm';
import { getFieldFormsResults } from 'utils/FieldTestsUtils';
import { CannotApproveAlert, NoActionsAlert } from 'components/Inspections/DrawerAlerts';
import { useAppContext } from 'hooks/useAppContext';
import { getInspection, InspectionAndFieldTestActions } from 'utils/InspectionsUtils';
import { useCanDoAction } from 'hooks/useCanDoAction';
import ViewInspectionsCard from './ViewInspectionCard';
import AuthCheck from 'components/Shared/AuthCheck';

const useStyles = makeStyles(theme => ({
  toolbar: theme.mixins.toolbar,
  drawerCloseButton: {
    width: "100%",
    borderRadius: 0,
    position: 'absolute',
    bottom: 0,
    borderTop: "1px solid rgba(0, 0, 0, 0.23)",
    background: theme.palette.background.default,
    height: 50
  },
  drawerDiv: {
    backgroundColor: theme.palette.background.default,
    width: "22vw",
    height: "calc(100% - 115px)",
    overflowY: 'scroll'
  },
  drawerItem: {
    padding: 16,
    background: theme.palette.background.default,
  }
}));

const ReviewDrawer = ({ rowData: _inspection, open, close, refreshGrid }) => {
  const classes = useStyles();
  const [fieldTests, setFieldTests] = useState({ ready: false, data: [] });
  const { selectedProject } = useAppContext();
  // need to store inspection data in component state because we need to be able to force update it after a review is submitted.
  const [inspection, setInspection] = useState({ ready: true, data: _inspection });
  const [updateDate, setUpdateDate] = useState(new Date());
  const [canApprove, setCanApprove] = useState(true);
  const [canReview, setCanReview] = useState(true);
  const [showActions, setShowActions] = useState(false);
  const canUpdateInspections = useCanDoAction('Inspections', 'update');

  // update local state of inspection if rowData prop changes;
  useEffect(() => {
    setInspection({ ready: true, data: _inspection });
  }, [_inspection]);


  // fetch data from API and hydrate component;
  useEffect(() => {
    const asyncEffect = async () => {
      const _fieldTestsReq = getFieldFormsResults(0, undefined, { field: "Description_TestResults" }, 'asc', undefined, [{ defaults: { projectID: selectedProject, inspectionNumber: inspection.data.InspectionNumber } }]);
      const __inspectionReq = getInspection(inspection.data.InspectionNumber);
      const [_fieldTests, __inspection] = await Promise.all([_fieldTestsReq, __inspectionReq]);
      // only need to update inspection data if the status changes
      __inspection.Description_InspectionStatus !== inspection.data.Description_InspectionStatus && setInspection({ ready: true, data: __inspection });
      setFieldTests({ ready: true, data: _fieldTests.data });
      setCanApprove(InspectionAndFieldTestActions.canApproveInspBasedOnFieldTests(_fieldTests.data));
      setCanReview(InspectionAndFieldTestActions.canReviewInspection(inspection.data.Description_InspectionStatus) && canUpdateInspections);
    }
    // resets to loader while making async call;
    setFieldTests({ ready: false, data: [] });

    asyncEffect();
  }, [selectedProject, updateDate, inspection, canUpdateInspections]);


  return (
    <>
      <Grid item xs={3}>
        <Drawer open={open} variant="persistent" anchor={'right'} onClose={close}>
          <div className={classes.toolbar} />
          <div className={classes.drawerDiv}>
            <div className={classes.drawerItem}>
              <Typography variant={"h5"}>Inspection {inspection.data.InspectionNumber}</Typography><br />
              <Chip label={`${inspection.data.Description_InspectionStatus}`} />
            </div>
            <Divider variant="middle" />
            <div className={classes.drawerItem} style={canApprove || !canReview ? { display: 'none' } : {}}>
              <CannotApproveAlert show={!canApprove} handleAction={() => setShowActions(e => !e)} />
            </div>
            <div className={classes.drawerItem} style={canReview ? { display: 'none' } : {}}>
              <NoActionsAlert show={!canReview} handleAction={close} />
            </div>
            <>
              <div className={classes.drawerItem}>
                <ViewInspectionsCard key={updateDate} inspection={inspection.data} />
              </div>
              {
                inspection.data.Description_InspectionStatus !== "Active" &&
                <AuthCheck scope="Inspections" action="update">
                  <div className={classes.drawerItem}>
                    <ReviewInspectionForm key={updateDate} inspection={inspection.data} fieldTests={fieldTests.data} refresh={() => { setUpdateDate(new Date()); refreshGrid(); }} disabled={!canReview} close={close} />
                  </div>
                </AuthCheck>
              }
              <Divider variant="middle" />
              <div className={classes.drawerItem}>
                <Typography variant="h6">Field Tests</Typography>
                {/*<Typography variant="subtitle2"></Typography>*/}
                <FieldTestCards fieldTests={fieldTests} refresh={() => setUpdateDate(new Date())} FieldTestCards={canApprove} showActions={showActions} />
              </div>
            </>
            <Button
              size="large"
              className={classes.drawerCloseButton}
              aria-label="close right drawer"
              onClick={close}
            >
              ≫
            </Button>
          </div>
        </Drawer>
      </Grid>
    </>
  )
}

export default ReviewDrawer;